import { TextButton } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ReviewEntry } from 'apis/merchant-gateway/insights/reviews/types';
import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { usePluralization } from 'common/hooks';
import { typographyCss } from 'common/typography';

import { isCompensationOfferAvailable } from './utils';

type OffersSummaryBannerProps = {
  timezone: string;
  reviews: ReviewEntry[];
  sentOffers: CreditAcquisition[];
  onFilterBadReviews: () => void;
};

const Banner = styled.div`
  background: ${colors.wolt8};
  padding: 16px;
`;

const Title = styled.h4`
  ${typographyCss.Body2}
  font-weight: 700;
  margin-block: 0 8px;
`;

const Body = styled.p`
  ${typographyCss.Body3}
  margin-block: 0 8px;
`;

const OffersSummaryBanner = ({ timezone, reviews, sentOffers, onFilterBadReviews }: OffersSummaryBannerProps) => {
  const { FormattedPluralMessage } = usePluralization();

  // Show a banner promoting sending offers if there are new bad reviews since the latest offer was sent (for the selected time period)
  const latestSentOfferTimestamp = Math.max(...sentOffers.map(offer => offer.acquiring_timestamp));
  const newReviewBreakpoint = latestSentOfferTimestamp
    ? DateTime.fromSeconds(latestSentOfferTimestamp).setZone(timezone)
    : undefined;
  const newBadRatingOrdersCount = reviews.filter(
    review =>
      isCompensationOfferAvailable(review.rating) &&
      (!newReviewBreakpoint || DateTime.fromISO(review.purchaseLocalTime, { zone: timezone }) > newReviewBreakpoint),
  ).length;

  if (!newBadRatingOrdersCount) {
    return null;
  }

  return (
    <Banner>
      <Title>
        <FormattedPluralMessage
          id="insights.ratings-and-reviews.offers-summary.new-ratings-banner-title"
          count={newBadRatingOrdersCount}
        />
      </Title>
      <Body>
        <FormattedMessage id="insights.ratings-and-reviews.offers-summary.new-ratings-banner-text" />
      </Body>
      <TextButton onClick={onFilterBadReviews}>
        <FormattedMessage id="insights.ratings-and-reviews.offers-summary.filter-low-rated-orders" />
      </TextButton>
    </Banner>
  );
};

export default OffersSummaryBanner;
